import { useCallback, useEffect, useMemo, useState } from 'react'
import { Icon } from '@iconify/react'
import { useDistributorWorkspace } from '@/contexts/DistributorWorkspaceContext'
import {
  fetchMyDistributorBalanceTransactions,
  formatMyDistributorCurrency,
  manualDistributorPrograms,
} from '@/lib/my-distributor'
import { RequireDistributorWorkspace } from '@/pages/SouDistribuidorPage/RequireDistributorWorkspace'
import type { MyDistributorBalanceTransaction } from '@/types/my-distributor'
import styles from './SouDistribuidorPage.module.css'

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function transactionLabel(transaction: MyDistributorBalanceTransaction): string {
  const base =
    transaction.type === 'commission_credit'
      ? 'Crédito de comissão'
      : transaction.type === 'payout_debit'
        ? 'Baixa de saldo'
        : 'Crédito simulado'

  if (transaction.source === 'affiliate' && transaction.affiliate_registration) {
    return `${base} (afiliado ${transaction.affiliate_registration.tracking_code})`
  }

  return base
}

function SouDistribuidorSaldoPageContent() {
  const { programs, selectedProgram, campaignId } = useDistributorWorkspace()
  const [transactions, setTransactions] = useState<MyDistributorBalanceTransaction[]>([])
  const [page, setPage] = useState(1)
  const [pagination, setPagination] = useState<{
    current_page: number
    last_page: number
    total: number
  } | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const manual = useMemo(() => manualDistributorPrograms(programs), [programs])

  const loadLedger = useCallback(
    async (distributorId: number, p: number) => {
      setIsLoading(true)
      setError(null)
      try {
        const res = await fetchMyDistributorBalanceTransactions(distributorId, {
          campaign_id: campaignId ?? undefined,
          page: p,
          per_page: 20,
        })
        setTransactions(res.items)
        setPagination({
          current_page: res.pagination.current_page,
          last_page: res.pagination.last_page,
          total: res.pagination.total,
        })
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao carregar extrato.')
      } finally {
        setIsLoading(false)
      }
    },
    [campaignId]
  )

  useEffect(() => {
    setPage(1)
  }, [selectedProgram?.distributor_id, campaignId])

  useEffect(() => {
    if (!selectedProgram || selectedProgram.payout_method !== 'manual') {
      setTransactions([])
      setPagination(null)
      setIsLoading(false)
      return
    }
    void loadLedger(selectedProgram.distributor_id, page)
  }, [selectedProgram, page, loadLedger])

  if (manual.length === 0) {
    return (
      <div className={styles.centered}>
        <Icon icon="mdi:wallet-outline" style={{ fontSize: '2rem' }} />
        <p>
          Você não possui programas com pagamento manual de comissão.
          <br />
          Nos programas com pagamento automático (Doey), a comissão é repassada no split do PIX.
        </p>
      </div>
    )
  }

  if (!selectedProgram || selectedProgram.payout_method !== 'manual') {
    return (
      <div className={styles.centered}>
        <Icon icon="mdi:wallet-outline" style={{ fontSize: '2rem' }} />
        <p>O programa selecionado usa pagamento automático (Doey).</p>
        <p className={styles.programMeta}>
          Selecione uma instituição com saldo manual no seletor do topo, se aplicável.
        </p>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <div className={styles.statsGridSecondary}>
        <div className={styles.card}>
          <h3 className={styles.cardTitle}>Saldo disponível</h3>
          <p style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, margin: 0 }}>
            {formatMyDistributorCurrency(selectedProgram.balance)}
          </p>
          <p className={styles.programMeta} style={{ marginTop: 'var(--spacing-sm)' }}>
            {selectedProgram.institution.name}
            {campaignId != null ? ' · filtro por campanha ativo' : ''}
          </p>
        </div>
        <div className={styles.card}>
          <h3 className={styles.cardTitle}>Comissões acumuladas</h3>
          <p style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, margin: 0 }}>
            {formatMyDistributorCurrency(selectedProgram.metrics.total_commission)}
          </p>
          <p className={styles.programMeta} style={{ marginTop: 'var(--spacing-sm)' }}>
            {selectedProgram.metrics.payments_count} venda(s) pagas · inclui movimentos de afiliados
          </p>
        </div>
      </div>

      {error ? (
        <div className={styles.centered}>
          <span>{error}</span>
        </div>
      ) : isLoading ? (
        <div className={styles.centered}>
          <Icon icon="mdi:loading" className={styles.spinner} />
        </div>
      ) : (
        <div className={styles.card}>
          <h3 className={styles.cardTitle}>Extrato consolidado</h3>
          {transactions.length === 0 ? (
            <div className={styles.emptyBlock}>
              <span>Nenhuma movimentação no extrato.</span>
            </div>
          ) : (
            <>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Origem</th>
                      <th>Tipo</th>
                      <th>Valor</th>
                      <th>Observação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((transaction) => (
                      <tr key={`${transaction.source}-${transaction.id}`}>
                        <td>{formatDate(transaction.created_at)}</td>
                        <td>
                          <span
                            className={
                              transaction.source === 'affiliate'
                                ? styles.sourceBadgeAffiliate
                                : styles.sourceBadge
                            }
                          >
                            {transaction.source === 'affiliate' ? 'Afiliado' : 'Distribuidor'}
                          </span>
                        </td>
                        <td>{transactionLabel(transaction)}</td>
                        <td>
                          {transaction.type === 'payout_debit' ? '−' : '+'}
                          {formatMyDistributorCurrency(transaction.amount)}
                        </td>
                        <td>{transaction.note ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {pagination && pagination.last_page > 1 ? (
                <div className={styles.pagination}>
                  <button
                    type="button"
                    className={styles.pageBtn}
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Anterior
                  </button>
                  <span>
                    Página {pagination.current_page} de {pagination.last_page}
                  </span>
                  <button
                    type="button"
                    className={styles.pageBtn}
                    disabled={page >= pagination.last_page}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Próxima
                  </button>
                </div>
              ) : null}
            </>
          )}
        </div>
      )}
    </div>
  )
}

export function SouDistribuidorSaldoPage() {
  return (
    <RequireDistributorWorkspace>
      <SouDistribuidorSaldoPageContent />
    </RequireDistributorWorkspace>
  )
}
